import { useState, useEffect } from 'react';
import { Timer, Calendar, Car } from '@phosphor-icons/react';
import AnimatedNumber from './AnimatedNumber';
import { useStudentTheme } from '../context/StudentThemeContext';

function calculateCountdown(examDate) {
    if (!examDate) return null;

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const exam = new Date(examDate);
    exam.setHours(0, 0, 0, 0);

    const diffMs = exam - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    return diffDays;
}

function getCountdownColor(days, themePrimary) {
    if (days === null) return '#4A5568';
    if (days <= 0) return themePrimary;
    if (days <= 3) return '#E53E3E';
    if (days <= 7) return '#DD6B20';
    if (days <= 14) return themePrimary;
    return '#38A169';
}

function getCountdownMessage(days, lessonsRemaining) {
    if (days === null) return '';
    if (days < 0) return 'Examen is al geweest';
    if (days === 0) return 'Vandaag is de grote dag!';
    if (days === 1) return 'Morgen is het zover!';
    if (days <= 3) return 'Bijna zover! Succes met de laatste voorbereiding';
    if (days <= 7) return 'Nog even volhouden, je bent er bijna!';
    if (lessonsRemaining > 0) return `Nog ${lessonsRemaining} lessen te gaan`;
    return 'Je bent goed op weg!';
}

export default function ExamCountdown({ examDate, examTime, examTableNumber, lessonsUsed, lessonsTotal }) {
    const theme = useStudentTheme();
    const [days, setDays] = useState(() => calculateCountdown(examDate));

    useEffect(() => {
        setDays(calculateCountdown(examDate));
        const interval = setInterval(() => {
            setDays(calculateCountdown(examDate));
        }, 60000);
        return () => clearInterval(interval);
    }, [examDate]);

    if (!examDate || days === null) return null;

    const color = getCountdownColor(days, theme.primary);
    const lessonsRemaining = lessonsTotal > 0 ? Math.max(0, lessonsTotal - lessonsUsed) : 0;
    const message = getCountdownMessage(days, lessonsRemaining);
    const isExamDay = days === 0;
    const isPast = days < 0;
    const progressPct = (!isPast && days > 0) ? Math.max(5, 100 - (days / 60) * 100) : 0;

    return (
        <div
            className="bg-[#1A202C] rounded-xl border-2 p-5 relative overflow-hidden"
            style={{ borderColor: color }}
            data-testid="exam-countdown"
        >
            <div
                className="absolute inset-0 opacity-5"
                style={{ background: `radial-gradient(circle at center, ${color}, transparent 70%)` }}
            />

            <div className="relative flex items-center gap-5">
                <div className={`text-center min-w-[90px] ${isExamDay ? 'animate-pulse-glow' : ''}`}>
                    {isPast ? (
                        <div className="text-4xl font-bold" style={{ color, fontFamily: 'Outfit, sans-serif' }}>
                            <Car size={48} weight="fill" className="mx-auto" />
                        </div>
                    ) : (
                        <>
                            <div
                                className="text-5xl font-bold leading-none"
                                style={{ color, fontFamily: 'Outfit, sans-serif' }}
                            >
                                <AnimatedNumber value={days} duration={1300} />
                            </div>
                            <div className="text-xs text-gray-500 mt-1 uppercase tracking-wider">
                                {days === 1 ? 'dag' : 'dagen'}
                            </div>
                        </>
                    )}
                </div>

                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <Timer size={18} style={{ color }} />
                        <span className="text-sm font-semibold text-white">
                            {isExamDay ? 'EXAMENDAG!' : isPast ? 'Examen geweest' : 'Examen Countdown'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-2 flex-wrap">
                        <Calendar size={14} />
                        <span>{examDate}{examTime ? ` om ${examTime}` : ''}</span>
                        {examTableNumber && (
                            <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold uppercase tracking-wider"
                                style={{ borderColor: color, color, backgroundColor: `${color}1A` }}
                                data-testid="exam-countdown-table-number"
                            >
                                Tafel {examTableNumber}
                            </span>
                        )}
                    </div>
                    <p className="text-sm" style={{ color }}>
                        {message}
                    </p>

                    {!isPast && days > 0 && (
                        <div className="mt-3">
                            <div className="w-full h-1.5 bg-[#2D3748] rounded-full overflow-hidden">
                                <div
                                    className="h-full rounded-full"
                                    style={{
                                        backgroundColor: color,
                                        width: `${progressPct}%`,
                                        transition: 'width 1.5s ease-out',
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
